import { useDispatch } from "react-redux";
import { ISeminarTaskBlock } from "../../../../../../../models/tasks";
import { useSelector } from "react-redux";
import { useState } from "react";
import {
  handleCancelTrackBlockSelection,
  handleSelectTrackBlock,
  canCancelBlock,
} from "../utils/blockUtils";
import ActionsButton from "../ActionsButton";
import TaskWrap from "../TaskWrap";

interface SelectedClassId {
  userSelectionId: string;
  classId: string;
}

interface TrackSelectionBlockProps {
  block: ISeminarTaskBlock;
  userSelection: any;
  task: string;
  eventId: string;
  status: string;
  allowSelect?: AllowSelectionAll;
  isProgramTeam: boolean;
  language?: string;
  getSelectedClassTrackIds: () => SelectedClassId[];
  isTaskSelectingInProgress: boolean;
  setIsTaskSelectingInProgress: (value: boolean) => void;
  allUserSelections?: { [taskId: string]: any[] };
}

interface AllowSelectionAll {
  allowSelectAll: boolean;
  minSelect: number;
  maxSelect: number;
}

const TrackSelectionBlock = ({
  block,
  userSelection,
  task,
  eventId,
  status,
  allowSelect,
  isProgramTeam,
  language,
  getSelectedClassTrackIds,
  isTaskSelectingInProgress,
  setIsTaskSelectingInProgress,
  allUserSelections,
}: TrackSelectionBlockProps) => {
  const dispatch = useDispatch();
  const { me } = useSelector((state: any) => state.auth);

  const [blockLoader, setBlockLoader] = useState<boolean>(false);
  const [isSelectingInProgress, setIsSelectingInProgress] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { Name, Classes__r = [], Block_Description__c } = block;
  const { records = [] } = userSelection || {};
  const countSelectedTask = records && records.length;

  const isTaskCompleted = status === "Completed";
  const remainingValues = Classes__r.flatMap((classItem) => {
    const value = classItem.Participants_Cap_Remaining__c as number | null | undefined;
    if (value === null || value === undefined) return [];
    const remaining = Number(value);
    return Number.isFinite(remaining) ? [remaining] : [];
  });
  const remainingCapacity = remainingValues.length > 0 ? Math.min(...remainingValues) : null;
  const isClassFull = remainingValues.some((value) => value <= 0);
  const BlockSelected = records?.find(
    (record) => record.Selected_Option__c === Name
  );
  const isBlockSelected = Boolean(BlockSelected);
  const classesIds = Classes__r.map((c) => c.Id);
  const isNotEnglish = language !== "English";

  const handleCancel = async () => {
    const selectedRecord = BlockSelected;
    setError(null);
    try {
      await handleCancelTrackBlockSelection(
        selectedRecord,
        task,
        me,
        eventId,
        dispatch,
        setBlockLoader,
        classesIds,
        block.Id,
        true
      );
    } catch (err) {
      setError(err.message || "Failed to cancel track block selection");
      throw err;
    }
  };

  const handleSelect = async () => {
    const trackClassesIds = getSelectedClassTrackIds();
    setError(null);
    try {
      await handleSelectTrackBlock(
        records,
        task,
        block,
        dispatch,
        me,
        eventId,
        setBlockLoader,
        classesIds,
        trackClassesIds,
        allowSelect,
        countSelectedTask
      );
    } catch (err) {
      setBlockLoader(false);
      if (err?.response?.status !== 409) {
        setError(err.message || "Failed to select track block");
      }
      throw err;
    }
  };

  const canCancelCurrentBlock = isBlockSelected ? canCancelBlock(block.Id, block, allUserSelections) : true;
  
  const isActionButtonDisabled = isTaskCompleted || (isClassFull && !isBlockSelected) || isProgramTeam || (isBlockSelected && !canCancelCurrentBlock);
  const isTaskSelectingInBackground = isTaskSelectingInProgress && !blockLoader;

  const handleSelectAction = async () => {
    if (isActionButtonDisabled || isSelectingInProgress || isTaskSelectingInBackground) return;
    setIsSelectingInProgress(true);
    setIsTaskSelectingInProgress(true);

    try {
      if (isBlockSelected) {
        await handleCancel();
      } else {
        await handleSelect();
      }
    } finally {
      setIsSelectingInProgress(false);
      setIsTaskSelectingInProgress(false);
    }
  }


  return (
    <div className={`d-flex align-items-center ${isBlockSelected && !canCancelCurrentBlock ? 'cancellation-disabled' : ''}`}
      onClick={handleSelectAction}
      title={isBlockSelected && !canCancelCurrentBlock ? "Cannot cancel: This selection is required by other dependencies" : ""}
      style={{
        pointerEvents: isSelectingInProgress || isTaskSelectingInBackground ? "none" : "auto",
        cursor: isSelectingInProgress || isTaskSelectingInBackground ? "not-allowed" : "pointer",
        opacity: isTaskSelectingInBackground ? 0.5 : 1
      }}>
      <ActionsButton
        isProgramTeam={isProgramTeam}
        isTaskCompleted={isTaskCompleted}
        isClassFull={isClassFull}
        isSelected={isBlockSelected}
        isLoading={blockLoader}
        isCancellationDisabled={isBlockSelected && !canCancelCurrentBlock}
      />
      <TaskWrap
        title={Name}
        description={Block_Description__c}
        isLoading={blockLoader}
        isSelected={isBlockSelected}
        isClassFull={isClassFull}
        isTaskCompleted={isTaskCompleted || isProgramTeam}
        isNotEnglish={isNotEnglish}
        taskInfo={remainingCapacity === null ? [] : [{
          name: "remainingCapacity",
          value: remainingCapacity,
          order: 0
        }]}
        language={language}
        error={error}
        isCancellationDisabled={isBlockSelected && !canCancelCurrentBlock}
      />
    </div>
  );
};

export default TrackSelectionBlock;